import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, Phone, Mail, MapPin, MessageSquare, Send, Calendar, Edit2,
  Building, User, Clock, FileText, Plus, CheckCircle, AlertCircle, Sparkles,
  StickyNote, PhoneCall, Mail as MailIcon, MessageCircle, File, Activity, IndianRupee,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import ProcedureSelect from './ProcedureSelect';
import {
  getEngageStatusBadge, getEngageSourceBadge,
  ENGAGE_STATUS_LABEL, ENGAGE_STATUS_ORDER, ENGAGE_SOURCE_LABEL,
} from '../../utils/engageStageBadge';
import { SOURCE_LINK_CONFIG, ENGAGE_SOURCE_OPTIONS } from '../../utils/engageSourceLinks';
import { cn } from '../../utils/tailwindUtils';
import ToastMsg from '../../components/common/ToastMsg';
import RecordingPlayer from './ai-caller/RecordingPlayer';
import CallButton from '../../components/telephony/CallButton';
import CallHistory from '../../components/telephony/CallHistory';
import { AuthContext } from '../../context/AuthContext';
import EmedhubAppointments from '../../components/engage/EmedhubAppointments';
import ConvertToPatientModal from './ConvertToPatientModal';


const LOG_TYPE_META = {
  all:      { label: 'All',      icon: Activity,       color: 'text-gray-500' },
  note:     { label: 'Notes',    icon: StickyNote,     color: 'text-amber-500' },
  call:     { label: 'Calls',    icon: PhoneCall,      color: 'text-green-500' },
  whatsapp: { label: 'WhatsApp', icon: MessageCircle,  color: 'text-emerald-500' },
  email:    { label: 'Email',    icon: MailIcon,       color: 'text-blue-500' },
  sms:      { label: 'SMS',      icon: MessageSquare,  color: 'text-cyan-500' },
  document: { label: 'Documents',icon: File,           color: 'text-purple-500' },
  status:   { label: 'Status',   icon: Activity,       color: 'text-indigo-500' },
};

const TAB_ORDER = ['all', 'note', 'call', 'whatsapp', 'email', 'sms', 'document', 'status'];


function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

function relativeTime(iso) {
  if (!iso) return '';
  const now = new Date();
  const then = new Date(iso);
  const secs = Math.round((now - then) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 86400 * 7) return `${Math.floor(secs / 86400)}d ago`;
  return then.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}


// ── Engagement gauge (doughnut using inline SVG) ─────────────────────────────
function EngagementGauge({ score = 0, grade = 'Cold' }) {
  const size = 120;
  const radius = 50;
  const stroke = 12;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, Math.min(100, score));
  const dash = (progress / 100) * circumference;

  const color =
    grade === 'Hot'  ? '#ef4444' :
    grade === 'Warm' ? '#f59e0b' :
                       '#94a3b8';

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} className="rotate-[-90deg]">
        <circle cx={size/2} cy={size/2} r={radius} strokeWidth={stroke}
                stroke="rgba(148,163,184,0.2)" fill="none" />
        <circle cx={size/2} cy={size/2} r={radius} strokeWidth={stroke}
                stroke={color} fill="none" strokeLinecap="round"
                strokeDasharray={`${dash} ${circumference}`}
                style={{ transition: 'stroke-dasharray 400ms' }} />
      </svg>
      <div className="-mt-[74px] flex flex-col items-center">
        <div className="text-2xl font-bold text-gray-900 dark:text-white">{Math.round(progress)}</div>
        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">/ 100</div>
      </div>
      <div className={cn(
        'mt-8 px-2.5 py-0.5 rounded-full text-[11px] font-medium',
        grade === 'Hot'  ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
        grade === 'Warm' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                           'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
      )}>
        {grade}
      </div>
    </div>
  );
}


// ── Info row (left panel) ────────────────────────────────────────────────────
function EditField({ label, value, onChange, type = 'text', required = false }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} required={required}
        className="w-full px-2 py-1.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
    </div>
  );
}


function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 py-1.5">
      {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
        <div className="text-sm text-gray-900 dark:text-white mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
}


export default function EnquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetch: apiFetch, create: apiCreate, update: apiUpdate } = useApiMethods({ throwOnFail: true });
  const { hasFeature } = useContext(AuthContext);
  const aiCallerEnabled = hasFeature('engage_ai_caller');

  const [enquiry, setEnquiry] = useState(null);
  const [logs, setLogs] = useState([]);
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [noteText, setNoteText] = useState('');
  const [nextFollowupDate, setNextFollowupDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [statusUpdating, setStatusUpdating] = useState(false);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  // /engage/enquiries/:id/convert opens the convert dialog straight away.
  const location = useLocation();
  const [converting, setConverting] = useState(location.pathname.endsWith('/convert'));
  const [users, setUsers] = useState([]);

  const [linkOptions, setLinkOptions] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const editLinkCfg = editing ? SOURCE_LINK_CONFIG[editForm.source] : null;

  const [aiCalling, setAiCalling] = useState(false);

  // Staff click-to-call. The call row is written when the provider accepts it,
  // but its status only moves once the phones ring — so reload a beat later.
  const [callRefreshKey, setCallRefreshKey] = useState(0);
  const onStaffCalled = () => setTimeout(() => setCallRefreshKey(k => k + 1), 3000);
  const handleAICall = async () => {
    if (!enquiry?.id || !enquiry?.mobile) return;
    if (!window.confirm(`Trigger AI voice call to ${enquiry.name} (${enquiry.mobile}) now?`)) return;
    setAiCalling(true);
    try {
      const resp = await apiCreate('engage/ai-caller/calls/initiate/', { enquiry_id: enquiry.id });
      if (resp?.status === 'success') {
        ToastMsg('success', 'AI call initiated');
      } else {
        ToastMsg('error', resp?.message || 'Failed to initiate call');
      }
    } catch (e) {
      ToastMsg('error', e?.response?.data?.message || 'Failed to initiate call');
    } finally {
      setAiCalling(false);
    }
  };

  useEffect(() => {
    apiFetch('auth/users/simple/').then(res => {
      const rows = res?.data || res?.results || [];
      setUsers(Array.isArray(rows) ? rows : []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When source changes in edit mode, refresh the linked-entity dropdown
  useEffect(() => {
    if (!editLinkCfg) { setLinkOptions([]); return; }
    let cancelled = false;
    setLinkLoading(true);
    apiFetch(editLinkCfg.endpoint).then(res => {
      if (cancelled) return;
      const rows = res?.results || res?.data || [];
      setLinkOptions((Array.isArray(rows) ? rows : []).map(editLinkCfg.mapOption));
    }).catch(() => { if (!cancelled) setLinkOptions([]); })
      .finally(() => { if (!cancelled) setLinkLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editForm.source]);

  const startEdit = () => {
    const cfg = SOURCE_LINK_CONFIG[enquiry?.source];
    const currentLinkedId = cfg ? enquiry?.source_details?.[cfg.detailsKey] : '';
    setEditForm({
      name: enquiry?.name || '',
      mobile: enquiry?.mobile || '',
      email: enquiry?.email || '',
      department_of_interest: enquiry?.department_of_interest || '',
      doctor_of_interest: enquiry?.doctor_of_interest || '',
      procedure_of_interest: enquiry?.procedure_of_interest || '',
      expected_treatment_value: enquiry?.expected_treatment_value ?? '',
      message: enquiry?.message || '',
      assigned_to: enquiry?.assigned_to || '',
      source: enquiry?.source || 'phone',
      _linkedEntityId: currentLinkedId || '',
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (savingEdit || !enquiry) return;
    setSavingEdit(true);
    try {
      const { _linkedEntityId, ...formFields } = editForm;
      const payload = { ...enquiry, ...formFields };
      // Backend expects assigned_to as ID or null (not empty string)
      if (payload.assigned_to === '' || payload.assigned_to === undefined) payload.assigned_to = null;

      // Blank expected_treatment_value → null so DRF treats it as "no quote entered"
      if (payload.expected_treatment_value === '' || payload.expected_treatment_value == null) {
        payload.expected_treatment_value = null;
      } else {
        payload.expected_treatment_value = Number(payload.expected_treatment_value);
      }

      // Rebuild source_details based on the chosen source
      const cfg = SOURCE_LINK_CONFIG[formFields.source];
      if (cfg && _linkedEntityId) {
        payload.source_details = { [cfg.detailsKey]: Number(_linkedEntityId) };
      } else if (cfg && cfg.optional) {
        // Left blank on purpose → drop the link, but keep anything else the
        // source recorded (e.g. Meta lead ids) when the source itself is unchanged.
        const rest = formFields.source === enquiry?.source ? { ...(enquiry?.source_details || {}) } : {};
        delete rest[cfg.detailsKey];
        payload.source_details = rest;
      } else if (!cfg) {
        // Source no longer needs a linked entity → clear source_details
        payload.source_details = {};
      }

      await apiUpdate(`/engage/enquiries/${id}/`, payload);
      setEditing(false);
      await reload();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const [aiCalls, setAiCalls] = useState([]);

  const reload = async () => {
    setLoading(true);
    try {
      const [eResp, lResp, engResp, cResp] = await Promise.all([
        apiFetch(`engage/enquiries/${id}/`),
        apiFetch(`engage/activity-logs/?enquiry=${id}`),
        apiFetch(`engage/analytics/patient-engagement/${id}/`),
        aiCallerEnabled
          ? apiFetch(`engage/ai-caller/calls/?enquiry_id=${id}&limit=50`).catch(() => null)
          : Promise.resolve(null),
      ]);
      setEnquiry(eResp?.data || eResp?.data?.data || null);
      const rawLogs = lResp?.results || lResp?.data?.results || [];
      setLogs(Array.isArray(rawLogs) ? rawLogs : []);
      setEngagement(engResp?.data || engResp?.data?.data || null);
      const rawCalls = cResp?.data || cResp?.data?.data || [];
      setAiCalls(Array.isArray(rawCalls) ? rawCalls : []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load enquiry');
    } finally {
      setLoading(false);
    }
  };

  // Match an ActivityLog(log_type='call') to an AICallLog by nearest ended_at (± 2 minutes).
  const findAICallForLog = React.useCallback((log) => {
    if (log.log_type !== 'call' || !(log.message || '').includes('[AI Caller]')) return null;
    const logMs = new Date(log.created_date).getTime();
    let best = null, bestDelta = Infinity;
    for (const c of aiCalls) {
      const stamp = c.ended_at || c.started_at || c.created_date;
      if (!stamp) continue;
      const delta = Math.abs(new Date(stamp).getTime() - logMs);
      if (delta < bestDelta && delta < 5 * 60 * 1000) {
        best = c; bestDelta = delta;
      }
    }
    return best;
  }, [aiCalls]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submitNote = async () => {
    if (!noteText.trim() || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        enquiry: Number(id),
        log_type: 'note',
        message: noteText.trim(),
      };
      if (nextFollowupDate) payload.follow_up_date = nextFollowupDate;
      await apiCreate('/engage/activity-logs/', payload);

      // Also update the Enquiry's followup_datetime so it flows to the list + engagement alerts
      if (nextFollowupDate && enquiry) {
        try {
          await apiUpdate(`/engage/enquiries/${id}/`, {
            ...enquiry,
            followup_datetime: `${nextFollowupDate}T09:00:00`,
          });
        } catch { /* non-fatal — the note still saved */ }
      }

      setNoteText('');
      setNextFollowupDate('');
      await reload();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save note');
    } finally {
      setSubmitting(false);
    }
  };

  // The enquiry's own ladder (Settings → Patient Types), so custom stages such
  // as RNR 1-3 can be picked. Enquiries with no patient type fall back to the
  // fixed status list.
  const [stageOptions, setStageOptions] = useState([]);
  useEffect(() => {
    if (!enquiry?.patient_type) { setStageOptions([]); return; }
    let cancelled = false;
    apiFetch(`engage/patient-types/${enquiry.patient_type}/stages/`).then(res => {
      if (cancelled) return;
      const rows = res?.results || res?.data?.results || [];
      setStageOptions(Array.isArray(rows) ? rows : []);
    }).catch(() => { if (!cancelled) setStageOptions([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiry?.patient_type]);

  const updateStage = async (stageId) => {
    if (statusUpdating || !enquiry) return;
    setStatusUpdating(true);
    try {
      // Status is left out: the server derives it from the stage.
      const { status: _ignored, ...rest } = enquiry;
      await apiUpdate(`/engage/enquiries/${id}/`, { ...rest, stage: Number(stageId) });
      await reload();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update stage');
    } finally {
      setStatusUpdating(false);
    }
  };

  const updateStatus = async (newStatus) => {
    if (statusUpdating || !enquiry) return;
    setStatusUpdating(true);
    try {
      await apiUpdate(`/engage/enquiries/${id}/`, {
        ...enquiry,
        status: newStatus,
      });
      await reload();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  // Show notes, status changes, and AI call summaries in the timeline
  const filteredLogs = useMemo(
    () => logs.filter(l => ['note', 'status', 'call'].includes(l.log_type)),
    [logs],
  );

  if (loading && !enquiry) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading enquiry…</div>
    );
  }
  if (error && !enquiry) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
          {error}
        </div>
        <button
          onClick={() => navigate('/engage/enquiries')}
          className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back to list
        </button>
      </div>
    );
  }
  if (!enquiry) return null;

  const sourceDetails = enquiry.source_details || {};
  const displaySource = ENGAGE_SOURCE_LABEL[enquiry.source] || enquiry.source || '—';

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      {/* Top nav / header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/engage/enquiries')}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Enquiries
        </button>
        <div className="flex items-center gap-2">
          {!editing ? (
            <>
              {enquiry?.mobile && (
                <CallButton enquiryId={enquiry.id} onCalled={onStaffCalled}
                  className="rounded-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700" />
              )}
              {aiCallerEnabled && (
                <button
                  onClick={handleAICall}
                  disabled={aiCalling || !enquiry?.mobile}
                  title={!enquiry?.mobile ? 'No mobile number on this enquiry' : 'Trigger AI voice call'}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 disabled:opacity-50"
                >
                  <Phone className="w-3.5 h-3.5" /> {aiCalling ? 'Calling…' : 'AI Call'}
                </button>
              )}
              <button
                onClick={startEdit}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Edit2 className="w-3.5 h-3.5" /> Edit
              </button>
            </>
          ) : (
            <>
              <button
                onClick={cancelEdit}
                disabled={savingEdit}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
              >Cancel</button>
              <button
                onClick={saveEdit}
                disabled={savingEdit || (editLinkCfg && !editLinkCfg.optional && !editForm._linkedEntityId)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-sm shadow-sm hover:bg-primary/90 disabled:opacity-50"
              >
                <Send className="w-3.5 h-3.5" /> {savingEdit ? 'Saving…' : 'Save changes'}
              </button>
            </>
          )}
          <Link
            to={`/engage/enquiries/${id}/engagement`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Activity className="w-3.5 h-3.5" /> Engagement
          </Link>
          {enquiry.converted_patient ? (
            <Link
              to={`/engage/patients/${enquiry.converted_patient}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 text-sm hover:bg-green-100"
            >
              <CheckCircle className="w-3.5 h-3.5" /> View patient
            </Link>
          ) : !editing && (
            <button
              onClick={() => setConverting(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm shadow-sm hover:bg-green-700"
            >
              <CheckCircle className="w-3.5 h-3.5" /> Convert to Patient
            </button>
          )}
        </div>
      </div>

      {converting && (
        <ConvertToPatientModal
          enquiry={enquiry}
          onClose={() => setConverting(false)}
          onConverted={() => { setConverting(false); reload(); }}
        />
      )}

      {/* Header card */}
      <div className="bg-gradient-to-r from-blue-500/5 to-purple-500/5 dark:from-blue-500/10 dark:to-purple-500/10 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-lg font-bold shadow-sm">
              {(enquiry.name || '?').charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">{enquiry.name}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageStatusBadge(enquiry.status))}>
                  {ENGAGE_STATUS_LABEL[enquiry.status] || enquiry.status}
                </span>
                <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageSourceBadge(enquiry.source))}>
                  {displaySource}
                </span>
                {enquiry.created_date && (
                  <span className="text-[11px] text-gray-500 dark:text-gray-400">
                    Created {fmtDateTime(enquiry.created_date)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Stage dropdown */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 dark:text-gray-400">Change stage:</label>
            {stageOptions.length > 0 ? (
              <select
                value={enquiry.stage || ''}
                onChange={e => updateStage(e.target.value)}
                disabled={statusUpdating}
                title={enquiry.patient_type_name ? `${enquiry.patient_type_name} stages` : undefined}
                className="text-sm px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
              >
                {!enquiry.stage && <option value="">—</option>}
                {stageOptions.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            ) : (
              <select
                value={enquiry.status}
                onChange={e => updateStatus(e.target.value)}
                disabled={statusUpdating}
                className="text-sm px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
              >
                {ENGAGE_STATUS_ORDER.map(s => (
                  <option key={s} value={s}>{ENGAGE_STATUS_LABEL[s]}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Alerts (engagement) */}
      {engagement?.alerts?.length > 0 && (
        <div className="space-y-2">
          {engagement.alerts.map((a, i) => (
            <div key={i} className={cn(
              'flex items-start gap-2 p-3 rounded-lg border text-sm',
              a.severity === 'high'
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
                : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300'
            )}>
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ── LEFT — Info card ───────────────────────────────────────────── */}
        <div className="lg:col-span-1 space-y-4">
          {/* Moving to Appointment Booked sends the enquiry to eMedHub (eMedHub-linked hospitals only). */}
          <EmedhubAppointments
            enquiryId={id}
            enquiryStatus={enquiry.status}
            converted={!!enquiry.converted_patient}
            onChanged={reload}
          />

          {/* Engagement gauge */}
          {engagement && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-purple-500" />
                  Engagement
                </h3>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                  {engagement.total_touchpoints} touchpoints
                </span>
              </div>
              <EngagementGauge score={engagement.score} grade={engagement.grade} />
              {engagement.last_contact_at && (
                <div className="mt-3 text-[11px] text-center text-gray-500 dark:text-gray-400">
                  Last contact: {relativeTime(engagement.last_contact_at)}
                </div>
              )}
            </div>
          )}

          {/* Contact + info */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Contact</h3>
            {editing ? (
              <div className="space-y-2">
                <EditField label="Name" value={editForm.name} onChange={v => setEditForm(f => ({ ...f, name: v }))} required />
                <EditField label="Mobile" value={editForm.mobile} onChange={v => setEditForm(f => ({ ...f, mobile: v }))} required />
                <EditField label="Email" value={editForm.email} onChange={v => setEditForm(f => ({ ...f, email: v }))} type="email" />
              </div>
            ) : (
              <>
                <InfoRow icon={Phone} label="Mobile" value={enquiry.mobile} />
                <InfoRow icon={Mail}  label="Email"  value={enquiry.email} />
              </>
            )}
          </div>

          <CallHistory enquiryId={enquiry.id} refreshKey={callRefreshKey} />

          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Interest</h3>
            {editing ? (
              <div className="space-y-2">
                <EditField label="Department" value={editForm.department_of_interest} onChange={v => setEditForm(f => ({ ...f, department_of_interest: v }))} />
                <EditField label="Doctor" value={editForm.doctor_of_interest} onChange={v => setEditForm(f => ({ ...f, doctor_of_interest: v }))} />
                <div>
                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Procedure</label>
                  <ProcedureSelect
                    value={editForm.procedure_of_interest || ''}
                    onChange={(v) => setEditForm(f => ({ ...f, procedure_of_interest: v }))}
                    onPickCatalog={(row) => {
                      setEditForm(f => {
                        const next = { ...f, procedure_of_interest: row.name };
                        if (!next.expected_treatment_value) {
                          next.expected_treatment_value = String(row.default_value || '');
                        }
                        if (!next.department_of_interest && row.department) {
                          next.department_of_interest = row.department;
                        }
                        return next;
                      });
                    }}
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Expected treatment value (₹)</label>
                  <input
                    type="number" min="0" step="500"
                    value={editForm.expected_treatment_value ?? ''}
                    onChange={e => setEditForm(f => ({ ...f, expected_treatment_value: e.target.value }))}
                    placeholder="Quoted amount / package price"
                    className="w-full px-2 py-1.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-[10px] text-gray-500 mt-0.5">Overrides department ARPP in revenue projection. Blank = use department average.</p>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Message</label>
                  <textarea rows={3} value={editForm.message} onChange={e => setEditForm(f => ({ ...f, message: e.target.value }))}
                    className="w-full px-2 py-1.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            ) : (
              <>
                <InfoRow icon={Building} label="Department" value={enquiry.department_of_interest} />
                <InfoRow icon={User}     label="Doctor"     value={enquiry.doctor_of_interest} />
                <InfoRow icon={FileText} label="Procedure"  value={enquiry.procedure_of_interest} />
                {enquiry.expected_treatment_value != null && Number(enquiry.expected_treatment_value) > 0 && (
                  <InfoRow icon={IndianRupee} label="Quoted value" value={`₹ ${Number(enquiry.expected_treatment_value).toLocaleString('en-IN')}`} />
                )}
                {enquiry.message && (
                  <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Message</div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{enquiry.message}</p>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Source & assignment</h3>
            {editing ? (
              <div className="py-1.5 space-y-2">
                <div>
                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Source</label>
                  <select value={editForm.source || ''} onChange={e => setEditForm(f => ({ ...f, source: e.target.value, _linkedEntityId: '' }))}
                    className="w-full px-2 py-1.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {ENGAGE_SOURCE_OPTIONS.map(s => (
                      <option key={s} value={s}>{ENGAGE_SOURCE_LABEL[s] || s}</option>
                    ))}
                  </select>
                </div>
                {editLinkCfg && (
                  <div>
                    <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                      {editLinkCfg.label}{!editLinkCfg.optional && <span className="text-red-500"> *</span>}
                    </label>
                    <select value={editForm._linkedEntityId || ''} onChange={e => setEditForm(f => ({ ...f, _linkedEntityId: e.target.value }))}
                      disabled={linkLoading}
                      className="w-full px-2 py-1.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50">
                      <option value="">{linkLoading ? 'Loading…' : linkOptions.length === 0 ? '— No entries yet —' : '— Select —'}</option>
                      {linkOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </div>
                )}
              </div>
            ) : (
              <InfoRow icon={MapPin} label="Source" value={displaySource} />
            )}
            {editing ? (
              <div className="py-1.5">
                <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Assigned to</label>
                <select value={editForm.assigned_to || ''} onChange={e => setEditForm(f => ({ ...f, assigned_to: e.target.value ? Number(e.target.value) : '' }))}
                  className="w-full px-2 py-1.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Unassigned —</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.login_fname || u.first_name || ''} {u.login_lname || u.last_name || ''} {(u.login_name || u.user_name) ? `(${u.login_name || u.user_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <InfoRow icon={User} label="Assigned to" value={enquiry.assigned_to_name} />
            )}
            <InfoRow icon={Calendar}  label="Follow-up"    value={enquiry.followup_datetime ? fmtDateTime(enquiry.followup_datetime) : null} />

            {/* Source details (channel-specific metadata) */}
            {Object.keys(sourceDetails).length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-1">
                {Object.entries(sourceDetails).map(([k, v]) => (
                  <div key={k} className="text-[11px] flex justify-between gap-2">
                    <span className="text-gray-500 dark:text-gray-400 capitalize">{k.replace(/_/g, ' ')}:</span>
                    <span className="text-gray-700 dark:text-gray-300 text-right break-all">{String(v)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT — Timeline ──────────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-4">
          {/* Note composer */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Add note</h3>
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="What did you discuss / what happened?"
              rows={3}
              className="w-full text-sm p-2.5 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex items-center justify-between gap-3 mt-2">
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                <Calendar className="w-3.5 h-3.5 text-blue-500" />
                <label>Next follow-up:</label>
                <input
                  type="date"
                  value={nextFollowupDate}
                  onChange={e => setNextFollowupDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="px-2 py-1 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {nextFollowupDate && (
                  <button type="button" onClick={() => setNextFollowupDate('')}
                    className="text-[10px] text-gray-500 hover:text-red-500">clear</button>
                )}
              </div>
              <button
                onClick={submitNote}
                disabled={!noteText.trim() || submitting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-sm shadow-sm hover:bg-primary/90 disabled:opacity-50"
              >
                <Send className="w-3.5 h-3.5" /> {submitting ? 'Saving…' : 'Save Note'}
              </button>
            </div>
          </div>

          {/* Timeline entries */}
          <div className="space-y-2">
            {filteredLogs.length === 0 ? (
              <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
                No notes yet. Add the first one above.
              </div>
            ) : (
              filteredLogs.map(log => {
                const M = LOG_TYPE_META[log.log_type] || LOG_TYPE_META.all;
                const aiCall = findAICallForLog(log);
                return (
                  <div key={log.id} className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
                    <div className="flex items-start gap-2.5">
                      <div className={cn('w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center', 'bg-gray-100 dark:bg-gray-700')}>
                        <M.icon className={cn('w-4 h-4', M.color)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                            {M.label}
                          </span>
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1 flex-shrink-0">
                            <Clock className="w-3 h-3" /> {relativeTime(log.created_date)}
                          </div>
                        </div>
                        {log.message && (
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 whitespace-pre-wrap">{log.message}</p>
                        )}
                        {aiCallerEnabled && aiCall?.recording_url ? (
                          <RecordingPlayer callId={aiCall.id} />
                        ) : aiCallerEnabled && aiCall ? (
                          <p className="mt-2 text-[11px] text-gray-500 italic">
                            Recording is being processed by Twilio — refresh in ~30s.
                          </p>
                        ) : null}
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          {log.follow_up_date && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded-full">
                              <Calendar className="w-3 h-3" /> Next follow-up: {new Date(log.follow_up_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          )}
                          {aiCall && aiCallerEnabled && (
                            <Link
                              to={`/engage/ai-caller/calls/${aiCall.id}`}
                              className="inline-flex items-center gap-1 text-[11px] text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 bg-teal-50 dark:bg-teal-900/20 px-2 py-0.5 rounded-full"
                            >
                              <PhoneCall className="w-3 h-3" /> Full transcript
                            </Link>
                          )}
                          {log.created_by_name && (
                            <span className="text-[11px] text-gray-500 dark:text-gray-400">by {log.created_by_name}</span>
                          )}
                        </div>
                        {log.from_status && log.to_status && log.from_status !== log.to_status && (
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            Stage: <span className={cn('inline-block px-1.5 py-0.5 rounded text-[10px]', getEngageStatusBadge(log.from_status))}>{ENGAGE_STATUS_LABEL[log.from_status] || log.from_status}</span>
                            {' → '}
                            <span className={cn('inline-block px-1.5 py-0.5 rounded text-[10px]', getEngageStatusBadge(log.to_status))}>{ENGAGE_STATUS_LABEL[log.to_status] || log.to_status}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
